import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import authService from '../services/authService';
import { hasRouteAccess } from '../services/privilegeService';

/**
 * PrivilegedRoute component checks if the current user has permission to access a specific route
 * It will redirect to login if not authenticated or to an unauthorized page if authenticated but not authorized
 * 
 * @param {Object} props Component props
 * @param {string} props.path The route path to check against stored privileges
 * @param {JSX.Element} props.children The components to render if authorized
 * @returns {JSX.Element} The children or a redirect
 */
const PrivilegedRoute = ({ path, children }) => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    checkAuthorization();
  }, [path, location]);

  const checkAuthorization = () => {
    // First check if token is valid
    const isAuth = authService.isAuthenticated();
    if (!isAuth) {
      setIsAuthorized(false);
      setLoading(false);
      return;
    }

    try {
      // Check if user has access to this route using the privilege service
      const hasAccess = hasRouteAccess(path);
      console.log(`Path access check for ${path}: ${hasAccess}`);
      setIsAuthorized(hasAccess);
    } catch (error) {
      console.error('Error checking route authorization:', error);
      setIsAuthorized(false);
    }
    
    setLoading(false);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!authService.isAuthenticated()) {
    // Redirect to login with return path
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return isAuthorized ? children : <Navigate to="/unauthorized" replace />;
};

export default PrivilegedRoute; 